import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, Navigation, X, Share2, Bell, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';

// Generate ICS file content
const generateICS = (event) => {
  const startDate = new Date(event.dates.start);
  const endDate = new Date(event.dates.end);
  endDate.setDate(endDate.getDate() + 1); // Add 1 day for all-day event

  const formatDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//L'Atelier by JS//Food Truck//FR
BEGIN:VEVENT
UID:${event.id}@atelierjs.ch
DTSTAMP:${formatDate(new Date())}
DTSTART;VALUE=DATE:${event.dates.start.replace(/-/g, '')}
DTEND;VALUE=DATE:${event.dates.end.replace(/-/g, '')}
SUMMARY:L'Atelier by JS - ${event.name}
DESCRIPTION:${event.description}
LOCATION:${event.address}
URL:https://www.atelierjs.ch
END:VEVENT
END:VCALENDAR`;

  return icsContent;
};

// Download ICS file
const downloadICS = (event) => {
  const icsContent = generateICS(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `latelierbyjs-${event.name.toLowerCase().replace(/\s+/g, '-')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  toast.success('Événement ajouté à votre calendrier!');
};

// Open directions
const openDirections = (event) => {
  const { lat, lng } = event.coordinates;
  const destination = encodeURIComponent(event.address);
  
  // Check if iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  if (isIOS) {
    window.open(`maps://maps.apple.com/?daddr=${lat},${lng}&q=${destination}`, '_blank');
  } else {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${destination}`, '_blank');
  }
};

// Share event
const shareEvent = async (event) => {
  const shareData = {
    title: `${event.name} - L'Atelier by JS`,
    text: `Retrouvez L'Atelier by JS à ${event.location} le ${event.displayDates}! 🚚`,
    url: window.location.href
  };

  try {
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      await navigator.clipboard.writeText(`${shareData.title}\n${shareData.text}\n${shareData.url}`);
      toast.success('Lien copié dans le presse-papier!');
    }
  } catch (err) {
    if (err.name !== 'AbortError') {
      toast.error('Erreur lors du partage');
    }
  }
};

// Event Detail Modal for mobile
export const EventDetailModal = ({ event, isOpen, onClose }) => {
  if (!event || !isOpen) return null;

  const handleNotify = () => {
    toast.success(`Rappel créé pour ${event.name}!`, {
      description: `Vous serez notifié avant le ${event.displayDates}`,
      duration: 4000
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Modal */}
          <motion.div
            data-testid="event-detail-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl max-h-[80vh] overflow-y-auto"
          >
            {/* Handle */}
            <div className="sticky top-0 bg-white pt-3 pb-2 flex justify-center rounded-t-3xl">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="close-modal-btn"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            {/* Content */}
            <div className="px-6 pb-8">
              {/* Status badge */}
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                  event.status === 'active' ? 'bg-emerald-500 text-white' :
                  event.status === 'past' ? 'bg-gray-400 text-white' :
                  'bg-[#97c7c3] text-black'
                }`}>
                  {event.status === 'active' && <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />}
                  {event.status === 'active' ? 'EN COURS' : event.status === 'past' ? 'TERMINÉ' : 'À VENIR'}
                </span>
                <span className="text-xs text-gray-500 font-dm">{event.type}</span>
              </div>

              {/* Title */}
              <h2 className="font-league text-2xl uppercase tracking-wide text-black mb-4">
                {event.name}
              </h2>

              {/* Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#baa25e] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-dm text-sm text-black">{event.address}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#baa25e] flex-shrink-0" />
                  <p className="font-dm text-sm text-black">{event.displayDates}</p>
                </div>
              </div>

              {/* Description */}
              <p className="font-dm text-sm text-gray-600 mb-6">
                {event.description}
              </p>

              {/* Action buttons */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => openDirections(event)}
                  className="flex items-center justify-center gap-2 bg-black text-white py-3 rounded-xl font-dm text-sm font-bold"
                  data-testid="directions-btn"
                >
                  <Navigation className="w-4 h-4" />
                  Y aller
                </motion.button>
                
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => downloadICS(event)}
                  className="flex items-center justify-center gap-2 bg-[#97c7c3] text-black py-3 rounded-xl font-dm text-sm font-bold"
                  data-testid="calendar-btn"
                >
                  <CalendarPlus className="w-4 h-4" />
                  Ajouter
                </motion.button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleNotify}
                  className="flex items-center justify-center gap-2 border-2 border-[#baa25e] text-[#baa25e] py-3 rounded-xl font-dm text-sm font-bold"
                  data-testid="notify-modal-btn"
                >
                  <Bell className="w-4 h-4" />
                  Rappel
                </motion.button>
                
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => shareEvent(event)}
                  className="flex items-center justify-center gap-2 border-2 border-gray-200 text-gray-700 py-3 rounded-xl font-dm text-sm font-bold"
                  data-testid="share-modal-btn"
                >
                  <Share2 className="w-4 h-4" />
                  Partager
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export { openDirections, downloadICS, shareEvent };
